import React, { useEffect, useState } from 'react';
import { Button, Form } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import ExcelDownloadReport from '../../components/common/excel/download';
import {
  faArrowRotateLeft,
  faRefresh,
  faSliders,
} from '@fortawesome/free-solid-svg-icons';
import DatePicker from './DatePicker';
import EditableDropDown from '../../components/common/EditableDropdown';
import { ErrorMessage } from '../scheme-management/Components/StyledComponents';
import { useDispatch, useSelector } from 'react-redux';
import { arrayToDropdownOptions } from '../../utils/sms/arrayToDropdownOptions';
import { updateSpinnerState } from '../../actions/spinner';
import { convertArrayToObj } from '../../utils/json';
import { updateToastInfo } from '../../actions/settings';
import { setVendorsListInStore } from '../../actions/vendors';
import { getVendorsListInRegion } from '../../parse-functions/vendors';
import { paidInputValidate, paidTripsColumns } from './utils';
import { getPaidTripsPaymentList } from '../../parse-functions/payments';
import { getMultipleTripsByTripNumbers } from '../../parse-functions/trips';
import PaidTripsModal from './PaidTripsModal';
import NewTable from '../../components/common/NewTable';
import { getPaymentsReport } from '../../parse-functions/reports';

const PaidTripPayments = () => {
  const initialFormInput = {
    fromDate: '',
    toDate: '',
    vendorName: '',
    invoiceNumber: '',
  };
  const [tripsData, setTripsData] = useState([]);
  const { t } = useTranslation();
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [formInput, setFormInput] = useState(initialFormInput);
  const [formErrors, setFormErrors] = useState(initialFormInput);
  const vendorsList = useSelector((state) => state?.vendors?.vendorsList || {});
  const [vendorOptions, setVendorOptions] = useState([]);
  const [selectedTripsData, setSelectedTripsData] = useState({});
  const [showPaidTripModal, setShowPaidTripModal] = useState(false);
  const dispatch = useDispatch();

  const handleFilterClicked = async () => {
    try {
      dispatch(updateSpinnerState(true));
      const inputValid = paidInputValidate(formInput, setFormErrors);
      if (!inputValid) {
        dispatch(updateSpinnerState(false));
        return;
      }
      const data = await getPaidTripsPaymentList({
        filters: {
          ...formInput,
          vendorId: vendorOptions[formInput.vendorName],
        },
      });
      setTripsData(data);
      dispatch(updateSpinnerState(false));
    } catch (error) {
      console.log(error);
      dispatch(updateSpinnerState(false));
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t(''),
          message: t('Failed to fetch data!'),
        })
      );
    }
  };

  const handleRowClick = async (row) => {
    try {
      const tripNumbers = row.tripNumbers;
      const res = await getPaymentsReport({
        tripNumbers,
        reportType: 'paidTrips',
      });
      setSelectedTripsData({ tripsData: res, invoiceData: row });
      setShowPaidTripModal(true);
    } catch (error) {
      dispatch(
        updateToastInfo({
          show: true,
          type: 'danger',
          title: t(''),
          message: t('Failed to fetch data!'),
        })
      );
    }
  };

  useEffect(() => {
    const fetchVendor = async () => {
      if (Object.keys(vendorsList).length > 0) {
        const vendorObj = {};
        Object.values(vendorsList).forEach(
          (item) => (vendorObj[item['vendorName']] = item['objectId'])
        );
        setVendorOptions(vendorObj);
        return;
      }
      dispatch(updateSpinnerState(true));
      try {
        const filters = {
          status: 'active',
          getAllVendors: true,
        };
        let vendors = await getVendorsListInRegion(filters);
        dispatch(
          setVendorsListInStore(
            convertArrayToObj(JSON.parse(JSON.stringify(vendors)), 'objectId')
          )
        );
        vendors = JSON.parse(JSON.stringify(vendors));
        const vendorObj = vendors?.reduce((acc, row) => {
          acc[row['vendorName']] = row['objectId'];
          return acc;
        }, {});
        setVendorOptions(vendorObj);
        dispatch(updateSpinnerState(false));
      } catch (e) {
        dispatch(updateSpinnerState(false));
        dispatch(
          updateToastInfo({
            show: true,
            type: 'danger',
            title: t('Error'),
            message: t(e.message),
          })
        );
      }
    };
    fetchVendor();
  }, []);
  return (
    <div className="p-3">
      <div>
        <h4>Paid Trips Payment Overview</h4>
      </div>
      <div className="d-flex mb-3 flex-end justify-content-end align-items-center">
        <div className="btn-toolbar mb-2 mb-md-0 gap-2">
          <Button
            variant={showFilterOptions ? 'primary' : 'white'}
            onClick={() => {
              setShowFilterOptions((prev) => !prev);
            }}
            style={{
              width: '150px',
              height: '38px',
              borderRadius: 0,
            }}
            size="sm"
          >
            <FontAwesomeIcon icon={faSliders} />
            &nbsp;&nbsp;{t('Filter')}
          </Button>
          <Button
            variant="white"
            onClick={() => {
              handleFilterClicked();
            }}
            style={{
              width: '150px',
              height: '38px',
              borderRadius: 0,
            }}
            size="sm"
          >
            <FontAwesomeIcon icon={faRefresh} />
            &nbsp;&nbsp;{t('Refresh')}
          </Button>

          <ExcelDownloadReport
            style={{ width: '150px', height: '38px', borderRadius: 0 }}
            size="sm"
            data={tripsData || []}
            fileName={'UnpaidTripsData.csv'}
          />
        </div>
      </div>
      {showFilterOptions && (
        <>
          <div
            className="table-settings mt-4 mb-4 p-3"
            style={{
              background: 'white',
              transition: 'max-height 0.7s ease-in-out',
            }}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '20px',
                width: '100%',
                backgroundColor: 'white',
              }}
            >
              <Form.Group>
                <Form.Label>From Date</Form.Label>
                <DatePicker
                  value={formInput['fromDate']}
                  setValue={(date) =>
                    setFormInput((prev) => ({
                      ...prev,
                      fromDate: date,
                    }))
                  }
                  isInvalid={formErrors['fromDate']}
                  errorMessage={t(formErrors['fromDate'])}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>To Date</Form.Label>
                <DatePicker
                  value={formInput['toDate']}
                  setValue={(date) =>
                    setFormInput((prev) => ({
                      ...prev,
                      toDate: date,
                    }))
                  }
                  isInvalid={formErrors['toDate']}
                  errorMessage={t(formErrors['toDate'])}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Vendor Name</Form.Label>
                <EditableDropDown
                  id={'vendorName'}
                  onChange={(e) =>
                    setFormInput((prev) => ({
                      ...prev,
                      vendorName: e.value,
                    }))
                  }
                  backgroundColor="#eaedf2"
                  isInvalid={formErrors['vendorName']}
                  value={formInput['vendorName']}
                  options={arrayToDropdownOptions(Object.keys(vendorOptions))}
                  placeholder={'Select Vendor Name'}
                />
                <ErrorMessage>{t(formErrors['vendorName'])}</ErrorMessage>
              </Form.Group>
              <Form.Group>
                <Form.Label>Invoice Number</Form.Label>
                <Form.Control
                  style={{
                    height: 38,
                    width: '100%',
                    backgroundColor: '#eaedf2',
                  }}
                  type="text"
                  value={formInput['invoiceNumber']}
                  isInvalid={formErrors['vehicleNumber']}
                  onChange={(e) => {
                    e.persist();
                    setFormInput((prev) => ({
                      ...prev,
                      invoiceNumber: e.target.value,
                    }));
                  }}
                  placeholder="Enter the Invoice No."
                />
                <ErrorMessage>{t(formErrors['invoiceNumber'])}</ErrorMessage>
              </Form.Group>
            </div>
            <div
              style={{
                gridColumn: 'span 3', // Span across all columns
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
              }}
            >
              <Button
                variant="white"
                onClick={() => setFormInput(initialFormInput)}
                style={{
                  width: '150px',
                  height: '38px',
                  borderRadius: 0,
                  background: '#EAEDF2',
                }}
              >
                <FontAwesomeIcon icon={faArrowRotateLeft} />
                &nbsp;&nbsp;{t('Reset')}
              </Button>
              <Button
                variant="white"
                onClick={handleFilterClicked}
                style={{
                  width: '150px',
                  height: '38px',
                  borderRadius: 0,
                  border: '1px solid #262B40',
                }}
              >
                <FontAwesomeIcon icon={faSliders} />
                &nbsp;&nbsp;{t('Apply Filter')}
              </Button>
            </div>
          </div>
        </>
      )}
      <NewTable
        data={tripsData}
        columns={paidTripsColumns()}
        handleRowClick={handleRowClick}
        id={'tripsPaymentsTable'}
      />
      {Object.keys(selectedTripsData).length > 0 && (
        <PaidTripsModal
          tripsData={selectedTripsData.tripsData}
          invoiceData={selectedTripsData.invoiceData}
          showModal={showPaidTripModal}
          closeModal={() => {
            setShowPaidTripModal(false);
            setSelectedTripsData({});
          }}
        />
      )}
    </div>
  );
};

export default PaidTripPayments;
